/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, Injector } from '@angular/core';
import { Observable, of, Subscriber } from 'rxjs';
import { BridgeCommands } from 'bridge-commands/bridge.commands';
import { AndroidHelperService, AndroidNativeResult } from '../android-helper/android-helper.service';
import { LoggingService } from '../logging/logging.service';
import { PlatformDetectorService } from '../platform-detector/platform-detector.service';
import { AndroidPluginManager } from './android-plugin/android-plugin-manager.service';
import { BridgeMockService } from './bridge.mocks.service';
import { FullBridgeCommand } from './bridge.service';
import { BrowserPluginManager } from './browser-plugin/browser-plugin-manager.service';
import { PluginService } from './plugin.service';

@Injectable()
export class AndroidService extends PluginService {
    private static REQUIRES_NATIVE_IMPL: string[] = ['Streaming'];

    private static requiresNativeImplementation(module: string): boolean {
        return AndroidService.REQUIRES_NATIVE_IMPL.includes(module);
    }

    constructor(
        loggingService: LoggingService,
        pluginManagerAndroid: AndroidPluginManager,
        pluginManagerBrowser: BrowserPluginManager,
        private androidHelperService: AndroidHelperService,
        bridgeMocks?: BridgeMockService
    ) {
        super(loggingService.getLogger('crimson/android'), pluginManagerAndroid, pluginManagerBrowser, bridgeMocks);
    }

    executeCommandInternal<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        params: ParamType,
        shouldLogPayload: boolean,
        shouldIncludePayloadInError: boolean
    ): Observable<ResponseType> {
        const fullBridgeCommand: FullBridgeCommand<ParamType> = this.makeFullBridgeCommand(
            command,
            params,
            command.legacyCommand !== AndroidService.EXTENSION_MESSAGE
        );
        const request: string = JSON.stringify(fullBridgeCommand);

        if (PlatformDetectorService.isAndroidPlatform()) {
            if (this.androidHelperService.doesCommandExist(command.module, command.method)) {
                const observable: Observable<ResponseType> = new Observable<ResponseType>(observer => {
                    const androidNativeObservable: Observable<AndroidNativeResult> =
                        this.androidHelperService.executeCommandNative(
                            fullBridgeCommand,
                            command.persistent,
                            shouldLogPayload
                        );

                    androidNativeObservable.subscribe(
                        (androidNativeResult: AndroidNativeResult) => {
                            this.handleSuccess<ResponseType>(
                                observer,
                                command.persistent,
                                androidNativeResult.response
                            );
                        },
                        (androidNativeResult: AndroidNativeResult) => {
                            this.handleFailure(
                                observer,
                                fullBridgeCommand,
                                androidNativeResult.errorCode,
                                androidNativeResult.errorMessage,
                                shouldLogPayload,
                                shouldIncludePayloadInError
                            );
                        },
                        () => {
                            this.logger.info('callback complete');
                        }
                    );
                });
                return observable;
            } else {
                if (
                    !AndroidService.requiresNativeImplementation(fullBridgeCommand.module) &&
                    this.handlerMap.has(fullBridgeCommand.module)
                ) {
                    return this.executeCommandUsingPlugin(
                        command,
                        params,
                        shouldLogPayload,
                        shouldIncludePayloadInError
                    );
                } else {
                    this.logger.info(
                        'Command not found',
                        JSON.stringify(shouldLogPayload ? fullBridgeCommand : fullBridgeCommand.command)
                    );
                    return of(null);
                }
            }
        } else {
            return new Observable(observer => {
                const errorLog = 'Not running on Android. Command: ' + shouldLogPayload ? request : '';
                this.logger.error(errorLog);
                this.handleFailure(
                    observer,
                    fullBridgeCommand,
                    500,
                    errorLog,
                    shouldLogPayload,
                    shouldIncludePayloadInError
                );
            });
        }
    }
}
